/**
 * Collection Detail Page
 * Shows collection configuration matching the reference:
 * - Fields & Layout section (field management with drag/drop)
 * - Collection Setup section (collection, note, icon, color, display template, hidden, singleton)
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ArrowLeft, Plus, Save, Loader2, Database, Trash2, Eye, EyeOff, Layout, FileText, Settings, ChevronDown, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
    useCollection,
    useUpdateCollection,
    useDeleteCollection,
    isSystemCollection,
} from '@/hooks/useCollections';
import { useFields } from '@/hooks/useFieldsQuery';
import { useRelations } from '@/hooks/useRelations';
import type { Field } from '@/api/fields';
import { NewFieldSheet } from './NewFieldSheet';
import { FieldDetailSheet } from './FieldDetailSheet';
import { FieldsManagement } from './FieldsManagement';
import { AirtableImportSheet } from './AirtableImportSheet';

// Collection meta interface matching reference
interface CollectionMeta {
    collection: string;
    note: string;
    icon: string;
    color: string;
    display_template: string;
    hidden: boolean;
    singleton: boolean;
    // Archive settings
    archive_field: string;
    archive_value: string;
    unarchive_value: string;
    archive_app_filter: boolean;
    // Sort settings
    sort_field: string;
    // Accountability
    accountability: 'all' | 'activity' | null;
}

const defaultMeta: CollectionMeta = {
    collection: '',
    note: '',
    icon: 'database',
    color: '',
    display_template: '',
    hidden: false,
    singleton: false,
    archive_field: '',
    archive_value: 'archived',
    unarchive_value: 'draft',
    archive_app_filter: true,
    sort_field: '',
    accountability: 'all',
};

// Icon options (subset of available icons)
const ICON_OPTIONS = [
    'database', 'folder', 'file', 'file-text', 'image', 'video', 'music',
    'user', 'users', 'settings', 'star', 'heart', 'bookmark', 'tag',
    'calendar', 'clock', 'mail', 'message-square', 'phone', 'map-pin',
    'shopping-cart', 'credit-card', 'package', 'truck', 'home', 'building',
];

// Color options
const COLOR_OPTIONS = [
    { value: '_none', label: 'Default' },
    { value: '#6644FF', label: 'Purple' },
    { value: '#2ECDA7', label: 'Green' },
    { value: '#3399FF', label: 'Blue' },
    { value: '#FFC23B', label: 'Yellow' },
    { value: '#E35169', label: 'Red' },
    { value: '#A2B5CD', label: 'Gray' },
];

export function CollectionDetailPage() {
    const { collection: collectionName } = useParams<{ collection: string }>();
    const navigate = useNavigate();

    const { data: collection, isLoading: collectionLoading } = useCollection(collectionName!);
    const { data: fields, isLoading: fieldsLoading } = useFields(collectionName!);
    const { data: allRelations = [] } = useRelations();
    const updateCollection = useUpdateCollection();
    const deleteCollection = useDeleteCollection();

    const [meta, setMeta] = useState<CollectionMeta>(defaultMeta);
    const [isDirty, setIsDirty] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [newFieldOpen, setNewFieldOpen] = useState(false);
    const [airtableImportOpen, setAirtableImportOpen] = useState(false);
    const [selectedField, setSelectedField] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Compute peer dependencies - fields in OTHER collections that reference this collection
    // These are M2O fields pointing to this collection
    const peerDependencies = allRelations
        .filter((relation) => {
            // Find relations where this collection is the "one" side (target of M2O)
            return (
                relation.meta?.one_collection === collectionName &&
                relation.meta?.many_collection &&
                relation.meta?.many_field
            );
        })
        .map((relation) => ({
            collection: relation.meta?.many_collection,
            field: relation.meta?.many_field,
        }));

    // Initialize meta from collection data
    useEffect(() => {
        if (collection?.meta) {
            setMeta({
                collection: collectionName || '',
                // Do not keep collection name inside meta when updating (schema uses collection column separately)
                note: collection.meta.note ?? '',
                icon: collection.meta.icon ?? 'database',
                color: collection.meta.color ?? '',
                display_template: collection.meta.display_template ?? '',
                hidden: collection.meta.hidden ?? false,
                singleton: collection.meta.singleton ?? false,
                archive_field: collection.meta.archive_field ?? '',
                archive_value: collection.meta.archive_value ?? 'archived',
                unarchive_value: collection.meta.unarchive_value ?? 'draft',
                archive_app_filter: collection.meta.archive_app_filter ?? true,
                sort_field: collection.meta.sort_field ?? '',
                accountability: collection.meta.accountability ?? 'all',
            });
        }
    }, [collection]);

    const updateMeta = <K extends keyof CollectionMeta>(key: K, value: CollectionMeta[K]) => {
        setMeta((prev) => ({ ...prev, [key]: value }));
        setIsDirty(true);
    };

    const handleSave = () => {
        if (!collectionName) return;

        // Backend expects flattened meta fields, not nested under "meta"
        const { collection: _omitCollection, ...payload } = meta as any;

        updateCollection.mutate(
            {
                collection: collectionName,
                payload,
            },
            {
                onSuccess: () => {
                    toast.success('Collection settings saved');
                    setIsDirty(false);
                },
                onError: (error: Error) => {
                    toast.error(`Failed to save: ${error.message}`);
                },
            }
        );
    };

    const handleDelete = async () => {
        if (!collectionName || isDeleting) return;

        setIsDeleting(true);

        try {
            // Delete the collection - the backend will handle:
            // 1. Dropping FK constraints from other tables
            // 2. Deleting related M2O fields in other collections
            // 3. Cleaning up all metadata
            // 4. Dropping the table
            await deleteCollection.mutateAsync(collectionName);
            toast.success('Collection deleted');
            navigate('/settings/data-model');
        } catch (error) {
            toast.error(`Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsDeleting(false);
        }
    };

    const isSystem = collectionName ? isSystemCollection(collectionName) : false;

    // Get string fields for archive field selection
    const stringFields = fields?.filter((f: Field) => f.type === 'string') ?? [];
    // Get integer fields for sort field selection
    const integerFields = fields?.filter((f: Field) => f.type === 'integer' || f.type === 'bigInteger') ?? [];

    // Loading state
    if (collectionLoading || fieldsLoading) {
        return (
            <div className="space-y-6 p-6">
                <div className="flex items-center gap-4">
                    <Skeleton className="h-10 w-10" />
                    <Skeleton className="h-8 w-64" />
                </div>
                <div className="grid gap-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Skeleton key={i} className="h-16 w-full" />
                    ))}
                </div>
            </div>
        );
    }

    if (!collection) {
        return (
            <div className="flex h-64 items-center justify-center">
                <p className="text-muted-foreground">Collection not found</p>
            </div>
        );
    }

    return (
        <div className="space-y-8 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center gap-3">
                        <div
                            className="flex h-10 w-10 items-center justify-center rounded-lg"
                            style={{
                                backgroundColor: meta.color ? `${meta.color}20` : 'hsl(var(--primary) / 0.1)',
                                color: meta.color || 'hsl(var(--primary))'
                            }}
                        >
                            <Database className="h-5 w-5" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-semibold">{collectionName}</h1>
                            <p className="text-sm text-muted-foreground">
                                {isSystem ? 'System Collection' : 'User Collection'}
                                {meta.singleton && ' · Singleton'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {!isSystem && (
                        <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                        </Button>
                    )}
                    <Button
                        onClick={handleSave}
                        disabled={!isDirty || updateCollection.isPending}
                    >
                        {updateCollection.isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="mr-2 h-4 w-4" />
                                Save
                            </>
                        )}
                    </Button>
                </div>
            </div>

            {/* Fields & Layout Section */}
            <section className="space-y-4 max-w-3xl">
                <div className="flex items-center justify-between border-b border-border pb-2">
                    <div className="flex items-center gap-2">
                        <Layout className="h-4 w-4 text-muted-foreground" />
                        <h2 className="text-lg font-semibold">Fields & Layout</h2>
                        <span className="text-xs text-yellow-600 dark:text-yellow-500">Saves automatically</span>
                    </div>
                </div>

                {/* Fields Management with drag & drop */}
                <FieldsManagement
                    collection={collectionName!}
                    fields={fields || []}
                    onFieldClick={(fieldName) => setSelectedField(fieldName)}
                    onFieldsReordered={() => {
                        // Refetch will happen automatically via React Query
                    }}
                />

                {/* Create Field Button with Airtable Import Option */}
                {!isSystem && (
                    <div className="flex gap-2">
                        <Button className="flex-1" onClick={() => setNewFieldOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Field
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline">
                                    <ChevronDown className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setNewFieldOpen(true)}>
                                    <Plus className="mr-2 h-4 w-4" />
                                    Create Field Manually
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setAirtableImportOpen(true)}>
                                    <Upload className="mr-2 h-4 w-4" />
                                    Import from Airtable
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )}
            </section>

            {/* Collection Setup Section */}
            <section className="space-y-6 max-w-3xl">
                <div className="flex items-center gap-2 border-b border-border pb-2">
                    <Settings className="h-4 w-4 text-muted-foreground" />
                    <h2 className="text-lg font-semibold">Collection Setup</h2>
                </div>

                <div className="space-y-6">
                    {/* Collection Name (readonly) */}
                    <div className="space-y-2">
                        <Label>Collection</Label>
                        <Input
                            value={meta.collection || collectionName}
                            disabled
                            className="font-mono bg-muted"
                        />
                    </div>

                    {/* Note */}
                    <div className="space-y-2">
                        <Label>Note</Label>
                        <Textarea
                            value={meta.note}
                            onChange={(e) => updateMeta('note', e.target.value)}
                            placeholder="Add a description for this collection..."
                            rows={2}
                            disabled={isSystem}
                        />
                    </div>

                    {/* Icon and Color */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Icon</Label>
                            <Select
                                value={meta.icon}
                                onValueChange={(v) => updateMeta('icon', v)}
                                disabled={isSystem}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select icon..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {ICON_OPTIONS.map((icon) => (
                                        <SelectItem key={icon} value={icon}>
                                            {icon}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Color</Label>
                            <Select
                                value={meta.color || '_none'}
                                onValueChange={(v) => updateMeta('color', v === '_none' ? '' : v)}
                                disabled={isSystem}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select color..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {COLOR_OPTIONS.map((color) => (
                                        <SelectItem key={color.value} value={color.value}>
                                            <div className="flex items-center gap-2">
                                                {color.value !== '_none' && (
                                                    <div
                                                        className="h-3 w-3 rounded-full"
                                                        style={{ backgroundColor: color.value }}
                                                    />
                                                )}
                                                {color.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Display Template */}
                    <div className="space-y-2">
                        <Label>Display Template</Label>
                        <Input
                            value={meta.display_template}
                            onChange={(e) => updateMeta('display_template', e.target.value)}
                            placeholder="{{field_name}} - {{another_field}}"
                            className="font-mono"
                            disabled={isSystem}
                        />
                        <p className="text-xs text-muted-foreground">
                            Template for displaying items. Use {'{{field_name}}'} syntax.
                        </p>
                    </div>

                    {/* Hidden and Singleton toggles */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center justify-between rounded-lg border border-border p-4">
                            <div className="flex items-center gap-3">
                                {meta.hidden ? (
                                    <EyeOff className="h-5 w-5 text-muted-foreground" />
                                ) : (
                                    <Eye className="h-5 w-5 text-muted-foreground" />
                                )}
                                <div>
                                    <Label>Hidden</Label>
                                    <p className="text-xs text-muted-foreground">Hide from app navigation</p>
                                </div>
                            </div>
                            <Switch
                                checked={meta.hidden}
                                onCheckedChange={(checked) => updateMeta('hidden', checked)}
                                disabled={isSystem}
                            />
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-border p-4">
                            <div className="flex items-center gap-3">
                                <FileText className="h-5 w-5 text-muted-foreground" />
                                <div>
                                    <Label>Singleton</Label>
                                    <p className="text-xs text-muted-foreground">Treat as single object</p>
                                </div>
                            </div>
                            <Switch
                                checked={meta.singleton}
                                onCheckedChange={(checked) => updateMeta('singleton', checked)}
                                disabled={isSystem}
                            />
                        </div>
                    </div>

                    {/* Archive Settings */}
                    <div className="space-y-4 rounded-lg border border-border p-4">
                        <h3 className="font-medium">Archive</h3>
                        <p className="text-sm text-muted-foreground">
                            Allow soft-deleting items instead of permanently deleting them.
                        </p>
                        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                            <div>
                                <Label>Enable app archive filter</Label>
                                <p className="text-xs text-muted-foreground">Show archive controls in the app for this collection.</p>
                            </div>
                            <Switch
                                checked={meta.archive_app_filter}
                                onCheckedChange={(checked) => updateMeta('archive_app_filter', checked)}
                                disabled={isSystem}
                            />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Archive Field</Label>
                                <Select
                                    value={meta.archive_field || '_none'}
                                    onValueChange={(v) => updateMeta('archive_field', v === '_none' ? '' : v)}
                                    disabled={isSystem}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select field..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="_none">-- None --</SelectItem>
                                        {stringFields.map((f: Field) => (
                                            <SelectItem key={f.field} value={f.field}>
                                                {f.field}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {meta.archive_field && (
                                <>
                                    <div className="space-y-2">
                                        <Label>Archive Value</Label>
                                        <Input
                                            value={meta.archive_value}
                                            onChange={(e) => updateMeta('archive_value', e.target.value)}
                                            placeholder="archived"
                                            disabled={isSystem}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Unarchive Value</Label>
                                        <Input
                                            value={meta.unarchive_value}
                                            onChange={(e) => updateMeta('unarchive_value', e.target.value)}
                                            placeholder="draft"
                                            disabled={isSystem}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Sort Settings */}
                    {!meta.singleton && (
                        <div className="space-y-4 rounded-lg border border-border p-4">
                            <h3 className="font-medium">Sort</h3>
                            <p className="text-sm text-muted-foreground">
                                Enable drag-and-drop sorting by selecting a field to store the sort order.
                            </p>
                            <div className="max-w-xs space-y-2">
                                <Label>Sort Field</Label>
                                <Select
                                    value={meta.sort_field || '_none'}
                                    onValueChange={(v) => updateMeta('sort_field', v === '_none' ? '' : v)}
                                    disabled={isSystem}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select field..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="_none">-- None --</SelectItem>
                                        {integerFields.map((f: Field) => (
                                            <SelectItem key={f.field} value={f.field}>
                                                {f.field}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}

                    {/* Accountability */}
                    <div className="space-y-4 rounded-lg border border-border p-4">
                        <h3 className="font-medium">Accountability</h3>
                        <p className="text-sm text-muted-foreground">
                            Track who created and modified items in this collection.
                        </p>
                        <div className="max-w-xs space-y-2">
                            <Label>Track Changes</Label>
                            <Select
                                value={meta.accountability ?? 'all'}
                                onValueChange={(v) => updateMeta('accountability', v === '_null' ? null : v as CollectionMeta['accountability'])}
                                disabled={isSystem}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All (Revisions & Activity)</SelectItem>
                                    <SelectItem value="activity">Activity Only</SelectItem>
                                    <SelectItem value="_null">None</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            </section>

            {/* Delete Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Collection</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3">
                                <p>
                                    Are you sure you want to delete the collection "{collectionName}"?
                                    This will delete the collection and all items in it. This action is permanent.
                                </p>
                                {peerDependencies.length > 0 && (
                                    <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
                                        <p className="text-sm font-medium text-destructive mb-2">
                                            The following fields will be deleted as well, as they are related to the current collection:
                                        </p>
                                        <ul className="list-disc list-inside text-sm text-destructive/90">
                                            {peerDependencies.map((dep) => (
                                                <li key={`${dep.collection}-${dep.field}`}>
                                                    {dep.field} ({dep.collection})
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isDeleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                'Delete'
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* New Field Sheet */}
            <NewFieldSheet
                open={newFieldOpen}
                onOpenChange={setNewFieldOpen}
                collection={collectionName!}
                onSuccess={() => setNewFieldOpen(false)}
            />

            {/* Airtable Import Sheet */}
            <AirtableImportSheet
                open={airtableImportOpen}
                onOpenChange={setAirtableImportOpen}
                collection={collectionName!}
                onSuccess={() => setAirtableImportOpen(false)}
            />

            {/* Field Detail Sheet */}
            {selectedField && (
                <FieldDetailSheet
                    open={!!selectedField}
                    onOpenChange={(open) => !open && setSelectedField(null)}
                    collection={collectionName!}
                    fieldName={selectedField}
                />
            )}
        </div>
    );
}
